import React from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';

const styles = theme => ({
  wrapper: {
    '& .react-datepicker__input-container': {
      width: '100%',
    },
  },
  datepicker: {
    boxShadow: `${theme.shadows[0]} !important`,
    border: '0.375rem !important',
    overflow: 'hidden !important',
    '& .react-datepicker__current-month, .react-datepicker-time__header, .react-datepicker__time-name': {
      fontSize: '0.875rem !important',
      fontWeight: '400 !important',
      lineHeight: '1.25rem !important',
      color: `${theme.palette.secondary.text} !important`,
    },
    '& .react-datepicker__header': {
      backgroundColor: `${theme.palette.white} !important`,
      borderColor: `${theme.palette.white} !important`,
      borderRadius: '0 !important',
      fontSize: '0.875rem !important',
      fontWeight: '400 !important',
      lineHeight: '3.125rem !important',
      paddingTop: '1rem !important',
      color: `${theme.palette.secondary['1000']} !important`,
      fontFamily: theme.typography.fontFamily,
    },
    '& .react-datepicker__day-names': {
      whiteSpace: 'nowrap !important',
      display: 'flex !important',
      alignItems: 'center !important',
      justifyContent: 'space-evenly !important',
      width: '18rem !important',
      margin: '0 auto !important',
      marginTop: '1rem !important',
      paddingTop: '1rem !important',
      borderTop: `0.063rem solid ${theme.palette.secondary['100']} !important`,
    },
    '& .react-datepicker__day-name': {
      color: `${theme.palette.gray.contrastText} !important`,
      fontSize: '0.75rem !important',
      fontWeight: '400 !important',
      lineHeight: '1rem !important',
    },
    '& .react-datepicker__month': {
      margin: '1rem 0.75rem !important',
      marginTop: '0.5rem !important',
    },
    '& .react-datepicker__navigation': {
      top: '1rem',
    },
    '& .react-datepicker__portal, .react-datepicker__navigation': {
      border: `0.7rem solid ${theme.palette.transparent} !important`,
    },
    '& .react-datepicker__portal, .react-datepicker__navigation--next': {
      borderLeftColor: `${theme.palette.gray.main} !important`,
    },
    '& .react-datepicker__portal, .react-datepicker__navigation--previous': {
      borderRightColor: `${theme.palette.gray.main} !important`,
    },
    '& .react-datepicker__day': {
      color: `${theme.palette.secondary.text} !important`,
      borderRadius: '0.375rem !important',
      border: `0.063rem solid ${theme.palette.transparent} !important`,
      fontFamily: `${theme.typography.fontFamily} !important`,
      fontSize: '0.875rem !important',
      fontWeight: '400 !important',
      lineHeight: '2.25rem !important',
      width: '2.25rem !important',
    },
    '& .react-datepicker__day--disabled': {
      cursor: 'default !important',
      color: `${theme.palette.gray.light} !important`,
    },
    '& .react-datepicker__day--keyboard-selected, .react-datepicker__day--selected': {
      backgroundColor: `${theme.palette.primary.main} !important`,
      transition: 'all 0.3s !important',
      color: `${theme.palette.white} !important`,
    },
    '& .react-datepicker__time-list-item--selected': {
      backgroundColor: `${theme.palette.primary.main} !important`,
      transition: 'all 0.3s !important',
      color: `${theme.palette.white} !important`,
    },
    '& .react-datepicker__time-container': {
      width: '5.625rem !important',
      borderLeft: `0.063rem solid ${theme.palette.secondary['100']} !important`,
    },
    '& .react-datepicker__time': {
      overflow: 'hidden !important',
    },
    '& .react-datepicker__time-container, .react-datepicker__time, .react-datepicker__time-box': {
      width: '5.625rem !important',
    },
    '& .react-datepicker__time-list': {
      padding: '0 !important',
    },
    '& .react-datepicker__time-list-item': {
      textAlign: 'right !important',
      fontFamily: theme.typography.fontFamily,
      whiteSpace: 'nowrap',
    },
  },
});

const DateTimePickerField = ({
  classes,
  className,
  minDate,
  maxDate,
  minTime,
  maxTime,
  withPortal,
  showTimeSelectOnly,
  dateFormat,
  showTimeSelect,
  customInput,
  timeFormat,
  selected,
  onChange,
  disabled,
}) => (
  <React.Fragment>
    <div className={`${className} ${classes.wrapper}`}>
      <DatePicker
        timeFormat={timeFormat}
        showTimeSelect={showTimeSelect}
        showTimeSelectOnly={showTimeSelectOnly}
        minDate={minDate}
        maxDate={maxDate}
        minTime={minTime}
        maxTime={maxTime}
        className="form-control"
        calendarClassName={classes.datepicker}
        dateFormat={dateFormat}
        selected={selected}
        onChange={onChange}
        withPortal={withPortal}
        customInput={customInput}
        disabled={disabled}
      />
    </div>
  </React.Fragment>
);

DateTimePickerField.propTypes = {
  classes: PropTypes.element.isRequired,
  className: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  minTime: PropTypes.instanceOf(Date),
  maxTime: PropTypes.instanceOf(Date),
  withPortal: PropTypes.bool,
  showTimeSelectOnly: PropTypes.bool,
  dateFormat: PropTypes.string,
  showTimeSelect: PropTypes.bool,
  customInput: PropTypes.element,
  timeFormat: PropTypes.string,
  selected: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

DateTimePickerField.defaultProps = {
  selected: '',
  timeFormat: 'p',
  className: '',
  disabled: false,
  minDate: null,
  maxDate: null,
  minTime: moment().startOf('day').toDate(),
  maxTime: moment().endOf('day').toDate(),
  withPortal: false,
  showTimeSelectOnly: false,
  showTimeSelect: true,
  // https://github.com/Hacker0x01/react-datepicker/issues/1609
  // can't set this from settings because can't find a unified solution
  dateFormat: 'MMMM d, yyyy h:mm aa',
  customInput: null,
  onChange: () => {},
};

export default withStyles(styles)(DateTimePickerField);
